import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from '@reach/router';
import { Subject } from 'api/lessonsApi';
import { LessonMenu, LessonMenuItem, SubjectSiteApi } from 'api/subjectSiteApi';
import { onEnterOrSpace } from './subjectSiteMenu';

type Props = {
  subject: Subject;
  activityLabel: string;
  activitiesLabel: string;
  isOpen: boolean;
  onClose: () => void;
};

type Level2Choice = { key: string; label: string; colorCSS: string | null };

const itemPath = (subject: Subject, keyStage: string, item: LessonMenuItem) =>
  `/${encodeURIComponent(
    subject.internalCode.toLowerCase()
  )}/lesson/${encodeURIComponent(keyStage.toLowerCase())}/${encodeURIComponent(
    item.activity.strippedName.toLowerCase()
  )}?years=${encodeURIComponent(item.years)}`;

const distinct = <T,>(values: T[], key: (value: T) => string) =>
  values.filter(
    (value, index) =>
      values.findIndex((other) => key(other) === key(value)) === index
  );

const panelLinkProps = (onSelect: () => void, bOpen: boolean) => ({
  href: '#',
  tabIndex: bOpen ? undefined : -1,
  onClick: (event: React.MouseEvent) => {
    event.preventDefault();
    onSelect();
  },
});

const navTitleProps = (onBack: () => void, bOpen = true) => ({
  role: 'button',
  tabIndex: bOpen ? 0 : -1,
  onClick: onBack,
  onKeyDown: onEnterOrSpace(onBack),
});

/**
 * The subject's lesson plan menu: three panels that slide in from the left,
 * key stage, then activity then year, or year then activity, as the
 * subject's lesson plan order says
 */
const SubjectSiteLessonMenu: React.FC<Props> = ({
  subject,
  activityLabel,
  activitiesLabel,
  isOpen,
  onClose,
}) => {
  const [menu, setMenu] = useState<LessonMenu | null>(null);
  const [bError, setBError] = useState(false);
  const [keyStage, setKeyStage] = useState<string | null>(null);
  const [level2, setLevel2] = useState<string | null>(null);

  const bErrorRef = useRef(false);
  const requestRef = useRef(0);

  const loadMenu = useCallback(() => {
    const request = ++requestRef.current;
    bErrorRef.current = false;
    setBError(false);
    SubjectSiteApi.getLessonMenu(subject.subjectsID).then((result) => {
      if (request !== requestRef.current) return;
      if (typeof result === 'string') {
        bErrorRef.current = true;
        setBError(true);
      } else {
        setMenu(result);
      }
    });
  }, [subject.subjectsID]);

  useEffect(() => {
    setMenu(null);
    loadMenu();
    const requests = requestRef;
    return () => {
      requests.current++;
    };
  }, [loadMenu]);

  // Opening the menu again retries a menu that failed to load
  useEffect(() => {
    if (!isOpen) {
      setKeyStage(null);
      setLevel2(null);
    } else if (bErrorRef.current) {
      loadMenu();
    }
  }, [isOpen, loadMenu]);

  const bYearFirst = menu?.lessonPlanOrder === 'KeySyage_Year_Activity';
  const selectedKeyStage = menu?.keyStages.find(
    (item) => item.strippedName === keyStage
  );
  const items = selectedKeyStage?.items ?? [];

  const level2Key = (item: LessonMenuItem) =>
    bYearFirst
      ? item.years
      : `${item.activity.strippedName}_${item.activity.bIsGroup ? 1 : 0}`;

  const level2Choices: Level2Choice[] = distinct(items, level2Key).map(
    (item) => ({
      key: level2Key(item),
      label: bYearFirst ? item.yearName : item.activity.name,
      colorCSS: bYearFirst ? null : item.activity.colorCSS,
    })
  );
  const selectedLevel2 = level2Choices.find((choice) => choice.key === level2);
  const level3Items = items.filter((item) => level2Key(item) === level2);
  const level3Colour = !bYearFirst ? selectedLevel2?.colorCSS : null;

  const closeLevel2 = () => {
    setKeyStage(null);
    setLevel2(null);
  };

  return (
    <>
      <div
        className={`LessonPlanMenu LessonNav_01${isOpen ? ' Opened' : ''}`}
        aria-hidden={!isOpen}
      >
        <div className="OverflowPanel1">
          <div className="Nav_Title" {...navTitleProps(onClose, isOpen)}>
            <span className="Nav_Back"></span>
            <span className="Nav_Txt">Key Stage</span>
          </div>
          {!isOpen ? null : bError ? (
            <p>An error occurred, please try again.</p>
          ) : (
            !menu && <p>Loading...</p>
          )}
          {menu && (
            <ul>
              {menu.keyStages.map((item) => (
                <li
                  key={item.strippedName}
                  className={
                    item.strippedName === keyStage ? 'Highlighted' : undefined
                  }
                >
                  <a
                    {...panelLinkProps(() => {
                      setKeyStage(
                        item.strippedName === keyStage
                          ? null
                          : item.strippedName
                      );
                      setLevel2(null);
                    }, isOpen)}
                  >
                    {item.name}
                    <span className="Nav_Arrow"></span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div
        className={`LessonPlanMenu LessonNav_02${
          selectedKeyStage ? ' Opened' : ''
        }`}
        aria-hidden={!selectedKeyStage}
      >
        {selectedKeyStage && (
          <div className={`OverflowPanel1${bYearFirst ? ' Grey' : ''}`}>
            <div className="Nav_Title" {...navTitleProps(closeLevel2)}>
              <span className="Nav_Back"></span>
              <span className="Nav_Txt">
                {bYearFirst ? 'Year' : activityLabel}
              </span>
            </div>
            <ul>
              {level2Choices.map((choice) => (
                <li
                  key={choice.key}
                  className={
                    [
                      choice.colorCSS ? `${choice.colorCSS}2` : '',
                      choice.key === level2 ? 'Highlighted' : '',
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                >
                  <a
                    {...panelLinkProps(
                      () =>
                        setLevel2(choice.key === level2 ? null : choice.key),
                      true
                    )}
                  >
                    {choice.label}
                    <span className="Nav_Arrow"></span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div
        className={`LessonPlanMenu LessonNav_03${
          level3Colour ? ` ${level3Colour}` : ''
        }${selectedKeyStage && selectedLevel2 ? ' Opened' : ''}`}
        aria-hidden={!(selectedKeyStage && selectedLevel2)}
      >
        {selectedKeyStage && selectedLevel2 && (
          <div className={`OverflowPanel1${bYearFirst ? ' White' : ''}`}>
            <div
              className="Nav_Title"
              {...navTitleProps(() => setLevel2(null))}
            >
              <span className="Nav_Back"></span>
              <span className="Nav_Txt">
                {bYearFirst ? activitiesLabel : 'Year'}
              </span>
            </div>
            <ul>
              {level3Items.map((item) => (
                <li
                  key={`${item.activity.strippedName}_${item.years}`}
                  className={
                    bYearFirst && item.activity.colorCSS
                      ? `${item.activity.colorCSS}2`
                      : undefined
                  }
                >
                  <Link
                    to={itemPath(subject, selectedKeyStage.strippedName, item)}
                    onClick={onClose}
                  >
                    {bYearFirst ? item.activity.name : item.yearName}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default SubjectSiteLessonMenu;
